import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/api";
import { PageTitle, GlassCard, EmptyState } from "@/components/ui-kit";
import InspireBanner from "@/components/InspireBanner";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Award, Download, Send, Sparkles, CheckCircle2, Zap, Crown, Trash2 } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const MAX = 5;

const nowMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const Bar = ({ label, value, weight, color }) => (
  <div className="flex items-center gap-2">
    <span className="text-[10px] text-stone-500 w-32 shrink-0 truncate">{label} <span className="text-stone-400">({weight}%)</span></span>
    <div className="flex-1 h-2 rounded-full bg-stone-200/70 overflow-hidden">
      <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(value, 100)}%`, background: color }} />
    </div>
    <span className="text-[10px] font-bold text-stone-600 w-9 text-left shrink-0">{Math.round(value)}%</span>
  </div>
);

export default function MonthlyCertificates() {
  const [month, setMonth] = useState(nowMonth());
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [delTarget, setDelTarget] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setSel(new Set());
    api.get(`/certificates/monthly/candidates?month=${month}`)
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const monthLabel = rows[0]?.month_label || month;

  const toggle = (id) => {
    setSel((prev) => {
      const n = new Set(prev);
      if (n.has(id)) { n.delete(id); return n; }
      if (n.size >= MAX) { toast.error(`الحد الأقصى ${MAX} شهادات دفعة واحدة`); return n; }
      n.add(id);
      return n;
    });
  };

  const autoTop = () => {
    const eligible = rows.filter((r) => !r.issued).slice(0, MAX);
    if (!eligible.length) return toast.info("لا يوجد منسقون مؤهلون للإصدار في هذا الشهر");
    setSel(new Set(eligible.map((r) => r.user_id)));
    toast.success(`تم تحديد الأعلى أداءً (${eligible.length})`);
  };

  const issue = async (ids) => {
    try {
      await api.post("/certificates/monthly/issue", { month, coordinator_ids: ids });
      toast.success(ids.length > 1 ? `تم إصدار وإرسال ${ids.length} شهادات 🏅` : "تم إصدار الشهادة وإرسالها لجرس المنسق 🏅");
      setConfirmOpen(false);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const download = async (userId, m) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/certificates/monthly/by-user/${userId}/pdf?month=${m}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `شهادة_${m}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("تعذّر تحميل الشهادة"); }
  };

  const removeCert = async () => {
    if (!delTarget) return;
    try {
      await api.delete(`/certificates/monthly/by-user/${delTarget.user_id}?month=${month}`);
      toast.success("تم حذف الشهادة");
      setDelTarget(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const rankColor = (i) => (i === 0 ? "#FFD700" : i === 1 ? "#C0C0C0" : i === 2 ? "#CD7F32" : "rgba(14,165,233,0.15)");

  return (
    <div className="space-y-6">
      <InspireBanner />
      <PageTitle title="الشهادات الشهرية" subtitle="محرك آلي يرتّب المنسقين حسب مؤشر الإنجاز الشهري — اعتمد شهادة الشكر بضغطة زر" icon={Award} />

      <GlassCard>
        <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
          <div className="flex items-center gap-2">
            <label className="text-sm text-stone-600">الشهر:</label>
            <input data-testid="month-picker" type="month" value={month} max={nowMonth()} onChange={(e) => setMonth(e.target.value)}
              className="px-3 py-2 rounded-lg bg-amber-50 border border-amber-400/30 text-stone-800 text-sm focus:outline-none focus:border-amber-400" />
            <span className="text-xs text-stone-500">{monthLabel}</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button data-testid="auto-top-btn" onClick={autoTop} className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg bg-sky-100 text-sky-700 hover:bg-sky-200 transition">
              <Zap size={15} /> تحديد الأعلى أداءً تلقائياً ({MAX})
            </button>
            <button data-testid="bulk-issue-btn" onClick={() => setConfirmOpen(true)} disabled={sel.size === 0}
              className="btn-gold flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg disabled:opacity-40">
              <Send size={15} /> إصدار وإرسال جماعي ({sel.size})
            </button>
          </div>
        </div>
        <p className="text-[11px] text-stone-500">المؤشر = نسبة المشاركة 40% + تحقيق المستهدف 90% (25%) + انتظام التحديث 20% + التفاعل مع التحديات 15%</p>
      </GlassCard>

      {loading ? (
        <div className="text-center text-stone-500 py-10">جارٍ حساب المؤشرات...</div>
      ) : !rows.length ? (
        <EmptyState text="لا يوجد منسقون بمدارس مسجّلة لعرض مؤشراتهم" icon={Award} />
      ) : (
        <div className="space-y-3">
          {rows.map((r, i) => (
            <GlassCard key={r.user_id} data-testid={`cand-row-${r.user_id}`}
              className={sel.has(r.user_id) ? "ring-2 ring-amber-400" : ""}>
              <div className="flex items-start gap-3">
                <input type="checkbox" data-testid={`cand-check-${r.user_id}`}
                  checked={sel.has(r.user_id)} disabled={r.issued}
                  onChange={() => toggle(r.user_id)}
                  className="mt-1 w-4 h-4 accent-amber-500 disabled:opacity-40" />
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                  style={{ background: rankColor(i), color: i < 3 ? "#1A120B" : "#0284C7" }}>
                  {i < 3 ? <Crown size={15} /> : i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="min-w-0">
                      <span className="text-sm font-bold text-stone-800">{r.coordinator_name || "—"}</span>
                      <span className="text-[11px] text-sky-700 mr-2">{r.school_name}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="inline-flex items-center gap-1 text-sm font-bold text-amber-600"><Sparkles size={14} /> {r.score}</span>
                      {r.issued ? (
                        <>
                          <span className="text-[10px] px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200 flex items-center gap-1"><CheckCircle2 size={12} /> صدرت</span>
                          <button data-testid={`dl-${r.user_id}`} onClick={() => download(r.user_id, month)} className="text-xs px-2.5 py-1.5 rounded-lg bg-sky-100 text-sky-700 hover:bg-sky-200 flex items-center gap-1"><Download size={13} /> PDF</button>
                          <button data-testid={`del-cert-${r.user_id}`} onClick={() => setDelTarget(r)} className="text-xs px-2.5 py-1.5 rounded-lg bg-red-100 text-red-700 hover:bg-red-200 flex items-center gap-1"><Trash2 size={13} /> حذف</button>
                        </>
                      ) : (
                        <button data-testid={`issue-${r.user_id}`} onClick={() => issue([r.user_id])} className="btn-gold text-xs px-3 py-1.5 rounded-lg flex items-center gap-1"><Award size={13} /> إصدار</button>
                      )}
                    </div>
                  </div>
                  <div className="mt-2 space-y-1.5">
                    <Bar label="نسبة المشاركة" weight={40} value={r.breakdown.participation} color="linear-gradient(90deg,#0EA5E9,#D4AF37)" />
                    <Bar label="تحقيق المستهدف 90%" weight={25} value={r.breakdown.target} color="#D4AF37" />
                    <Bar label="انتظام التحديث" weight={20} value={r.breakdown.regularity} color="#0EA5E9" />
                    <Bar label="التحديات" weight={15} value={r.breakdown.challenges} color="#10B981" />
                  </div>
                </div>
              </div>
            </GlassCard>
          ))}
        </div>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="glass border-amber-400/30 text-stone-800" dir="rtl">
          <AlertDialogHeader>
            <AlertDialogTitle>إصدار جماعي للشهادات</AlertDialogTitle>
            <AlertDialogDescription>
              سيتم توليد {sel.size} شهادة شكر شهرية عن {monthLabel} وإرسالها فوراً إلى لوحات المنسقين وجرس التنبيهات لديهم. هل تريد المتابعة؟
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-bulk-issue" onClick={() => issue([...sel])} className="btn-gold">إصدار وإرسال</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => { if (!o) setDelTarget(null); }}>
        <AlertDialogContent className="glass border-amber-400/30 text-stone-800" dir="rtl">
          <AlertDialogHeader>
            <AlertDialogTitle>حذف الشهادة</AlertDialogTitle>
            <AlertDialogDescription>
              سيتم حذف شهادة {delTarget?.coordinator_name || "المنسق"} ({delTarget?.school_name}) عن {monthLabel} نهائياً، وإزالتها من لوحة المنسق وجرس التنبيهات. هل تريد المتابعة؟
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-del-cert" onClick={removeCert} className="bg-red-600 hover:bg-red-700 text-white">حذف نهائي</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
